import { Box, CircularProgress, Button, Typography } from "@mui/material";
import GridList from "../../../ui/components/List/GridList";
import GridListItem from "../../../ui/components/List/GridListItem";
import { useExtraCourses } from "../api/getExtraCourses";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLastMenuPath } from "../../../shared/store/menu/menu";
import { imagesUrl } from "../../../shared/config";
import moment from "moment";
import { AccessTime } from "@mui/icons-material";
import DatesLabel from "../../../ui/components/DatesLabel";
import CertificateButton from "../../../ui/pages/TrackPage/ui/components/CertificateButton";
import ProgressTracker from "../../../ui/components/ProgressTracker";
import { useState } from "react";
import { requestExtraCourseEnrollment } from "../api/requestCourseEnrollment";
import RequestCourseEnrollment from "./RequestCourseEnrollment";
import { useQueryClient } from "@tanstack/react-query";

const ExtraCourseList = ({ filters }) => {
  const { data, isLoading } = useExtraCourses({
    config: {
      select: (_data) => {
        return _data
          .filter((course) => {
            if (filters.ids.length) {
              return filters.ids.includes(String(course.disciplinaid));
            }
            return true;
          })
          .filter((course) => {
            switch (filters.status) {
              case "Todos":
                return true;
              case "Expirados":
                return course.isExpired;
              case "Em andamento":
                return course.hasEnrollment && !course.isFinished;
              case "Concluídos":
                return course.isFinished;
              case "Sem matrícula":
                return !course.hasEnrollment;
              default:
                return false;
            }
          })
          .filter((course) => {
            if (!filters.search) {
              return true;
            } else {
              const regex = new RegExp("(" + filters.search + ")", "i");
              return regex.test(course.nome_disciplina);
            }
          });
      },
    },
  });
  const [selectedCourse, setSelectedCourse] = useState();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  if (!data?.length)
    return (
      <div className="absolute top-0 right-0 bottom-0 left-0 flex align-middle justify-center items-center">
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Typography variant="captions" color="GrayText">
            Sem resultados
          </Typography>
        )}
      </div>
    );
  return (
    <RequestCourseEnrollment
      courseData={selectedCourse}
      mutationFn={requestExtraCourseEnrollment}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: ["extraCourses"] });
      }}
    >
      {(dialog) => (
        <GridList>
          {data?.map((course, index) => {
            const {
              accessLabel,
              elapsedTimePercentage,
              canBeAccessedNow,
              remainingDays,
              isFinished,
              sendCertificate,
              waitingEnrollmentApproval,
              isEnrollmentExternal,
              selfSubscribe,
            } = course;
            const imageUrl =
              course.foto?.search(/http/i) > -1
                ? course.foto.replace("\\", "//")
                : `${imagesUrl}/${course.foto.replace("\\", "//")}`;

            return (
              <GridListItem
                ActionAreaProps={{
                  disabled: true,
                  onClick: () => {
                    dispatch(setLastMenuPath(location.pathname));
                    navigate(`/curso/${course.cursoid}/${course.disciplinaid}`);
                  },
                }}
                imageUrl={imageUrl}
                label={course.nome_disciplina}
                footer={
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexWrap: "wrap",
                        "& > *": {
                          paddingY: 1,
                          flex: "50%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        },
                      }}
                    >
                      <Box sx={{ flexDirection: "row" }}>
                        <AccessTime color="primary" sx={{ fontSize: "2rem" }} />

                        <Typography sx={{ marginLeft: 1 }} fontSize="0.75rem">
                          Duração <br />
                          <b>{course.duracao_dias} dias</b>
                        </Typography>
                      </Box>
                      {!!course.porcentagem && !course.isExpired && (
                        <Box hidden={true} sx={{ flexDirection: "column" }}>
                          <Typography variant="caption">Andamento</Typography>
                          <ProgressTracker
                            sx={{ width: "70px", marginRight: "5px" }}
                            color="secondary"
                            progress={course.porcentagem}
                          />
                          <Typography variant="caption">
                            {course.porcentagem}%
                          </Typography>
                        </Box>
                      )}
                      {Boolean(remainingDays) && (
                        <Box
                          sx={{
                            flexDirection: "row",
                            display:
                              elapsedTimePercentage >= 0 ? "flex" : "none",
                          }}
                        >
                          <ProgressTracker
                            sx={{ width: "30px", height: "30px" }}
                            variant="clock"
                            color="secondary"
                            progress={100 - elapsedTimePercentage}
                          />
                          <Typography sx={{ marginLeft: 1 }} fontSize="0.75rem">
                            {" "}
                            <b>{remainingDays} dias</b> <br /> restantes
                          </Typography>
                        </Box>
                      )}

                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            position: "relative",
                          }}
                        >
                          <DatesLabel
                            datesArray={[
                              {
                                date: moment(course.dtliberacao).format(
                                  "DD/MM/YYYY"
                                ),
                              },
                              {
                                date: moment(course.dtlimite).format(
                                  "DD/MM/YYYY"
                                ),
                              },
                            ]}
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        marginTop: "auto",
                        padding: 2,
                        gap: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        alignItems: "center",
                        button: {
                          width: "100%",
                        },
                        "& > button:nth-child(2)": {
                          color: "#22C55E",
                          borderColor: "#22C55E",
                        },
                      }}
                    >
                      {course.hasEnrollment ? (
                        <Button
                          onClick={() => {
                            dispatch(
                              setLastMenuPath(
                                location.pathname + location.search
                              )
                            );
                            navigate(
                              `/curso/${course.cursoid}/${course.disciplinaid}`
                            );
                          }}
                          variant="outlined"
                          disabled={!canBeAccessedNow}
                          color={canBeAccessedNow ? "success" : "info"}
                        >
                          {accessLabel}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{ color: "white" }}
                          disabled={waitingEnrollmentApproval}
                          onClick={() => {
                            console.log({ isEnrollmentExternal });
                            if (isEnrollmentExternal) {
                              window.open(course?.link_loja_externa, "_blank");
                              return;
                            }
                            setSelectedCourse(course);
                            dialog.open();
                          }}
                        >
                          {waitingEnrollmentApproval
                            ? "Aguardando liberação"
                            : isEnrollmentExternal
                            ? "Comprar"
                            : selfSubscribe
                            ? "Inscreva-se"
                            : "Solicitar inscrição"}
                        </Button>
                      )}

                      {isFinished && sendCertificate && (
                        <CertificateButton
                          customButton={<Button />}
                          course={{
                            matricula_conclusa: course.matriculaid,
                            DISCIPLINAID: course.disciplinaid,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                }
              />
            );
          })}
        </GridList>
      )}
    </RequestCourseEnrollment>
  );
};

export default ExtraCourseList;
